import * as Yup from "yup";

const FILE_SIZE_LIMIT = 2 * 1024 * 1024;

// Initial values for the updated CreateForm usage
const expenseRequestInitialValues = {
  member_name: "",
  date_of_birth: null,
  cin: "",
  health_plan: null, // expecting { value, label }
  county: null, // expecting { value, label }
  department_code: "",
  case_manager: null, // expecting { value, label }
  landlord_name: "",
  // Array of authorizations coming from backend for display/calculations
  authorizations: [],
  // UI expenses list
  expenses: [
    {
      paymentMethod: null, // { value, label }
      category: null, // { value, label }
      subcategory: null, // { value, label }
      amount: "",
      // Flags for persistence behavior
      _destroyed: false,
      new_created: true,
      // Optional id when editing existing expenses
      id: undefined,
    },
  ],
  upload_w9: null,
  upload_lease_agreement: null,
  upload_authorization: null,
  check_request_document: null,
  other_document: null,
  upload_receipt: null,
  // Files bucket (UI only; adapt as needed when integrating file upload)
  files: {},
  additional_comments: "",
};

// Validation schema adapted to UI fields and business rules
const validationSchema = Yup.object({
  member_name: Yup.string().nullable(),
  date_of_birth: Yup.mixed().nullable(),
  cin: Yup.string().nullable(),
  health_plan: Yup.mixed().nullable(),
  county: Yup.mixed().nullable(),
  department_code: Yup.number()
    .typeError("Department code must be a number")
    .integer("Department code must be an integer")
    .min(1000, "Department code must be exactly 4 digits")
    .max(9999, "Department code must be exactly 4 digits")
    .required("Department code is required"),
  case_manager: Yup.mixed().nullable(),
  landlord_name: Yup.string().nullable(),
  additional_comments: Yup.string()
    .max(1000, "Comments must be 1000 characters or less")
    .required("Additional Comments is required"),
  authorizations: Yup.array().of(
    Yup.object({
      type: Yup.string().nullable(),
      authNo: Yup.string().nullable(),
      startDate: Yup.mixed().nullable(),
      endDate: Yup.mixed().nullable(),
      approvedDays: Yup.number().nullable(),
      authorizedAmount: Yup.number().nullable(),
    }),
  ),

  upload_w9: Yup.mixed()
    .required("Document is Required")
    .test(
      "fileSize",
      "File size must be less than or equal to 2 MB",
      (value) => !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
    ),

  upload_authorization: Yup.mixed()
    .required("Document is Required")
    .test(
      "fileSize",
      "File size must be less than or equal to 2 MB",
      (value) => !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
    ),

  check_request_document: Yup.mixed()
    .required("Document is Required")
    .test(
      "fileSize",
      "File size must be less than or equal to 2 MB",
      (value) => !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
    ),
  upload_lease_agreement: Yup.mixed()
    .test(
      "fileSize",
      "File size must be less than or equal to 2 MB",
      (value) => !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
    )
    .when("status", {
      is: "approved",
      then: (schema) => schema.required("Document is Required"),
      otherwise: (schema) => schema.notRequired(),
    }),

  other_document: Yup.mixed()
    .required("Document is Required")
    .test(
      "fileSize",
      "File size must be less than or equal to 2 MB",
      (value) => !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
    ),

  upload_receipt: Yup.mixed().when(["_action", "status"], {
    is: (action, status) =>
      action === "complete" || status?.toLowerCase() === "approved",
    then: (schema) =>
      schema
        .required("Receipt is Required")
        .test(
          "fileSize",
          "File size must be less than or equal to 2 MB",
          (value) =>
            !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
        ),
    otherwise: (schema) =>
      schema
        .notRequired()
        .test(
          "fileSize",
          "File size must be less than or equal to 2 MB",
          (value) =>
            !value || !value.file || value.file.size <= FILE_SIZE_LIMIT,
        ),
  }),

  expenses: Yup.array()
    .of(
      Yup.object({
        paymentMethod: Yup.object()
          .shape({ value: Yup.string(), label: Yup.string() })
          .nullable()
          .when("_destroyed", {
            is: true,
            then: (s) => s.notRequired(),
            otherwise: (s) => s.required("Payment Method is required"),
          }),
        category: Yup.object()
          .shape({ value: Yup.string(), label: Yup.string() })
          .nullable()
          .when("_destroyed", {
            is: true,
            then: (s) => s.notRequired(),
            otherwise: (s) => s.required("Category is required"),
          }),
        subcategory: Yup.object()
          .shape({ value: Yup.string(), label: Yup.string() })
          .nullable()
          .when("_destroyed", {
            is: true,
            then: (s) => s.notRequired(),
            otherwise: (s) => s.required("Subcategory is required"),
          }),
        amount: Yup.number()
          .transform((value, original) => (original === "" ? undefined : value))
          .when("_destroyed", {
            is: true,
            then: (s) => s.notRequired(),
            otherwise: (s) =>
              s
                .required("Amount is required")
                .positive("Amount must be positive"),
          }),

        _destroyed: Yup.boolean().optional(),
        new_created: Yup.boolean().optional(),
        id: Yup.mixed().optional(),
      }),
    )
    .test(
      "at-least-one-active-expense",
      "At least one expense is required",
      function(value) {
        if (!value || value.length === 0) return false;
        const active = value.filter((e) => !e._destroyed);
        return active.length >= 1;
      },
    ),
});

// Backwards-compat export names expected by CreateForm import
const expenseRequestValidationSchema = validationSchema;

export {
  expenseRequestInitialValues,
  validationSchema,
  expenseRequestValidationSchema,
};
